<script lang="ts">
  import { Separator } from "$lib/components/ui/separator"
  import * as Tooltip from "$lib/components/ui/tooltip"
  import { isCompetitionEnded } from "$lib/components/Widget2/utils"
  import Action from "./Action/Action.svelte"
  import userStore, { userStoreActions } from "$lib/stores/userStore"
  import { statsStoreActions, statsStore } from "$lib/stores/statsStore"
  import { TrophyIcon, LogOut, LogIn, Users } from "lucide-svelte"
  import SpaceIcon from "$lib/components/Auth/social-icons/SpaceIcon.svelte"
  import { t, getTextDirection } from "$lib/utils/i18n"
  import type { Competition } from "$lib/types"

  interface Props {
    competition: Competition
    isCountryAllowed?: boolean
    isLoading?: boolean
    embed: boolean
    openTermsDialog: () => void
    toggleReferrals: () => void
    showReferrals: boolean
  }

  let {
    competition,
    isCountryAllowed = true,
    isLoading = $bindable(true),
    embed,
    openTermsDialog,
    toggleReferrals,
    showReferrals,
  }: Props = $props()

  // Get text direction for RTL support
  const textDirection = $derived(getTextDirection(competition?.language))
  const isRTL = $derived(textDirection === "rtl")

  let isEnded = isCompetitionEnded(competition.endDate)

  const handleActionClick = (event: CustomEvent<{ actionId: string }>) => {
    const clickedActionId = event.detail.actionId

    // If clicking the already open action, close it
    if ($userStore.openActionId === clickedActionId) {
      userStoreActions.setOpenActionId(null)
    } else {
      // Otherwise, open the clicked action (which automatically closes others)
      userStoreActions.setOpenActionId(clickedActionId)
    }
  }

  const handleLogout = () => {
    userStoreActions.clearUser()
    statsStoreActions.setStats({
      ...$statsStore,
      yourEntries: 0,
      referralCount: 0,
      referrals: [],
    })
  }

  const handleLogin = () => {
    // Get the first active action
    const activeActions = competition?.competitionActions
      ?.filter((a) => a.isActive)
      ?.sort((a, b) => a.order - b.order)

    if (activeActions && activeActions.length > 0) {
      const firstActionId = activeActions[0].id
      // Trigger the action click handler
      handleActionClick(
        new CustomEvent("actionClick", { detail: { actionId: firstActionId } }),
      )
    }
  }
</script>

{#if $userStore.user}
  <Separator />
  <div
    class="flex justify-between items-center text-sm text-gray-600 mb-2 pt-2"
    style="direction: {textDirection};"
  >
    <Tooltip.Provider delayDuration={0}>
      <Tooltip.Root>
        <Tooltip.Trigger>
          <p>{t("widget_logged_in_as")} {$userStore.user.name}</p>
        </Tooltip.Trigger>
        <Tooltip.Content>
          <p>{$userStore.user.email}</p>
        </Tooltip.Content>
      </Tooltip.Root>
    </Tooltip.Provider>
    <div class="flex items-center gap-2" class:flex-row-reverse={isRTL}>
      {#if $statsStore.referrals && $statsStore.referrals.length > 0}
        <button
          class="flex text-gray-500 hover:text-gray-700 items-center text-xs"
          class:flex-row-reverse={isRTL}
          onclick={toggleReferrals}
          aria-label="Toggle referrals"
        >
          <span class="mr-1" class:mr-0={isRTL} class:ml-1={isRTL}>
            {showReferrals ? "Hide" : "View"} Referrals ({$statsStore.referralCount})
          </span>
          <Users size={14} />
        </button>
      {/if}
      <button
        class="flex text-gray-500 hover:text-gray-700 items-center text-xs"
        class:flex-row-reverse={isRTL}
        onclick={handleLogout}
        aria-label="Log out"
      >
        <span class="mr-1" class:mr-0={isRTL} class:ml-1={isRTL}
          >{t("widget_logout")}</span
        >
        <LogOut size={14} />
      </button>
    </div>
  </div>
  <Separator />
{:else}
  <Separator />
  <div
    class="flex justify-between items-center text-sm text-gray-600 mb-2 pt-2"
    style="direction: {textDirection};"
  >
    <div></div>
    <div class="flex items-center gap-2" class:flex-row-reverse={isRTL}>
      <button
        class="flex text-gray-500 hover:text-gray-700 items-center text-xs"
        class:flex-row-reverse={isRTL}
        onclick={handleLogin}
        aria-label="Log in"
      >
        <span class="mr-1" class:mr-0={isRTL} class:ml-1={isRTL}>Login</span>
        <LogIn size={14} />
      </button>
    </div>
  </div>
  <Separator />
{/if}

<h3
  class="font-semibold my-2 text-left"
  class:text-right={isRTL}
  class:text-left={!isRTL}
  style="color: {competition?.widgetCustomizations?.colorSettings
    ?.primaryTextColor}; direction: {textDirection};"
>
  {t("widget_ways_to_earn_entries", {
    count: competition.competitionActions.length,
  })}
  - {t("widget_do_more_and_boost_odds")}
</h3>
<div class="space-y-2">
  {#if isCountryAllowed}
    {#if !isEnded}
      {#each competition?.competitionActions
        .filter((a) => a.isActive)
        .sort((a, b) => a.order - b.order) as action (action.id)}
        <Action
          {action}
          {competition}
          isOpen={$userStore.openActionId === action.id}
          bind:isLoading
          {embed}
          {openTermsDialog}
          on:actionClick={handleActionClick}
        />
      {/each}
    {:else}
      <div
        class="text-center my-10 h-[100px] flex flex-col justify-center items-center"
      >
        <div class="flex items-center bg-yellow-100 rounded-full p-6">
          <TrophyIcon class="size-10 text-yellow-600" />
        </div>
        <span class="text-black text-lg">{t("widget_competition_ended")}</span>
      </div>
    {/if}
  {:else}
    <div
      class="text-center my-10 h-[300px] flex flex-col justify-center items-center"
    >
      <SpaceIcon />
      <span class="text-black text-lg mt-4">
        {t("widget_not_offered_in_location")}
      </span>
    </div>
  {/if}
</div>
